<script lang="ts" setup>
import type { GetTeamsQuery, GetTeamsQueryVariables } from './TeamModal.generated'
import TeamModalBots from './TeamModalBots.vue'
import TeamModalMembers from './TeamModalMembers.vue'
import TeamModalRepositories from './TeamModalRepositories.vue'
import { CreateTeamMutationDocument, GetTeamsDocument, UpdateTeamMutationDocument } from './TeamModal.generated'

export type Mode = 'create' | 'edit'
export type Section = 'general' | 'members' | 'bots' | 'repositories'

interface Team {
  id: string
  name: string
}

const props = defineProps<{ mode: Mode; id?: string; initialSection?: Section }>()
const open = defineModel<boolean>({ required: true })

const form = useTemplateRef<HTMLFormElement>('form')
const membersRef = useTemplateRef<InstanceType<typeof TeamModalMembers>>('members')
const botsRef = useTemplateRef<InstanceType<typeof TeamModalBots>>('bots')
const reposRef = useTemplateRef<InstanceType<typeof TeamModalRepositories>>('repos')
const activeSection = ref<Section>('general')

// Simple form state: just name, parent, and provider (read-only).
// Members/bots/repositories live inside their respective subcomponents.
const state = reactive<{
  name: string
  parentTeam: Team
  provider: string | null
}>({ name: '', parentTeam: { id: '', name: '' }, provider: null })

const isGithubSynced = computed(() => state.provider === 'GITHUB')

const { fetchTeam, isFetching: isLoadingTeamDetails } = useTeam()

const { isFetching: isFetchingTeams, search: searchTeams } = useRelayField<
  GetTeamsQuery,
  GetTeamsQueryVariables,
  Team[]
>(
  GetTeamsDocument,
  { first: 50, term: '' },
  ({ data }) => getListOfNodes(data?.organization.teams).filter((node) => node.id !== props.id),
  ['teams'],
)

const { execute: createTeam, isFetching: isCreatingTeam } = useMutation(CreateTeamMutationDocument, {
  refetchTags: ['teams', 'team-members', 'team-repositories'],
})

const { execute: updateTeam, isFetching: isUpdatingTeam } = useMutation(UpdateTeamMutationDocument, {
  refetchTags: ['teams', 'team-members', 'team-repositories'],
})

const navItems = computed(() => [
  { label: 'General', icon: 'light:gear', section: 'general' as Section },
  { label: 'Members', icon: 'light:user-group', section: 'members' as Section },
  { label: 'Bots', icon: 'light:robot', section: 'bots' as Section },
  { label: 'Repositories', icon: 'light:code-branch', section: 'repositories' as Section },
])

const isLoadingData = computed(
  () => isLoadingTeamDetails.value || Boolean(membersRef.value?.isLoading) || Boolean(reposRef.value?.isLoading),
)

watch(open, async (newOpen) => {
  if (!newOpen) return

  activeSection.value = props.initialSection ?? 'general'
  state.name = ''
  state.parentTeam = { id: '', name: '' }
  state.provider = null

  // Modal contents mount on open, so wait a tick before touching child refs.
  await nextTick()

  membersRef.value?.reset()
  botsRef.value?.reset()
  reposRef.value?.reset()

  if (props.mode === 'edit' && props.id) {
    const [team] = await Promise.all([fetchTeam(props.id), membersRef.value?.load(props.id)])
    state.name = team.name
    state.provider = team.provider
    state.parentTeam = team.parent
  }
})

const isSaving = ref(false)

async function save(): Promise<{ ok: boolean; teamId?: string }> {
  const finalMemberIds = membersRef.value?.getFinalMemberIds() ?? []
  const finalRepos = reposRef.value?.getFinalRepos() ?? []

  let savedTeamId: string | undefined = props.id
  if (!isGithubSynced.value) {
    let response
    if (props.mode === 'create') {
      response = await createTeam({
        input: {
          name: state.name,
          members: finalMemberIds,
          skillsRepositories: finalRepos,
          parent: state.parentTeam.id,
        },
      })
    } else {
      response = await updateTeam({
        input: {
          id: props.id ?? '',
          name: state.name,
          members: finalMemberIds,
          skillsRepositories: finalRepos,
          parent: state.parentTeam.id,
        },
      })
    }

    const error = handleMutationError({ errors: response.data?.mutateTeam?.errors, combinedError: response.error })
    if (error) return { ok: false }

    savedTeamId = response.data?.mutateTeam?.team?.id ?? props.id
  }

  if (savedTeamId) {
    const adminOk = (await membersRef.value?.applyAdminChanges(savedTeamId)) ?? true
    if (!adminOk) return { ok: false }
    const botsOk = (await botsRef.value?.applyBotChanges(savedTeamId)) ?? true
    if (!botsOk) return { ok: false }
  }

  return { ok: true, teamId: savedTeamId }
}

async function onSubmit() {
  if (isSaving.value) return
  const name: HTMLInputElement = form.value?.$el.querySelector('[name="name"]')
  if (name && !name.checkValidity()) {
    name.reportValidity()
    return
  }
  isSaving.value = true
  try {
    const { ok, teamId } = await save()
    if (!ok) return
    open.value = false
    if (props.mode === 'create' && teamId) {
      await navigateTo({ name: 'teams-teamId', params: { teamId } })
    }
  } finally {
    isSaving.value = false
  }
}

function clearParent() {
  state.parentTeam = { id: '', name: '' }
}
</script>

<template>
  <UModal v-model="open" :prevent-close="isSaving" :ui="{ width: 'w-full sm:max-w-3xl' }">
    <UCard class="bg-white" :ui="{ body: { padding: 'p-0 sm:p-0' }, footer: { padding: 'px-4 py-3 sm:px-6' } }">
      <UButton
        icon="light:xmark"
        variant="ghost"
        color="gray"
        class="absolute right-2 top-2 z-10"
        :disabled="isSaving"
        @click="open = false"
      />

      <div class="flex h-[32rem]">
        <!-- Left Navigation -->
        <div class="flex w-48 shrink-0 flex-col border-r border-gray-200 p-4 dark:border-gray-700">
          <div class="mb-4 text-base font-semibold">
            {{ mode === 'create' ? 'New team' : 'Edit team' }}
          </div>
          <nav class="flex flex-col gap-0.5">
            <button
              v-for="item in navItems"
              :key="item.section"
              class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors"
              :class="[
                activeSection === item.section
                  ? 'bg-gray-100 font-medium text-gray-900 dark:bg-gray-800 dark:text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
              ]"
              @click="activeSection = item.section"
            >
              <UIcon :name="item.icon" class="size-4 shrink-0" />
              {{ item.label }}
            </button>
          </nav>
        </div>

        <!-- Right Content -->
        <div class="flex min-w-0 flex-1 flex-col overflow-hidden p-6">
          <UForm ref="form" :state="state" class="flex min-h-0 flex-1 flex-col" @submit="onSubmit">
            <!-- General Section -->
            <div v-show="activeSection === 'general'">
              <div class="mb-4 text-lg font-semibold">General</div>
              <UTooltip
                :text="SYNC_DISABLED_TOOLTIP"
                :prevent="!isGithubSynced"
                class="block"
                :ui="{ base: 'h-auto !whitespace-normal max-w-xs' }"
              >
                <FormGroup
                  label="Name"
                  required
                  class="mb-4"
                  size="md"
                  name="name"
                  help="You can use your department or team name. Keep it simple."
                  :disabled="isGithubSynced || isLoadingTeamDetails || isCreatingTeam || isUpdatingTeam"
                >
                  <UInput v-model="state.name" autofocus required :disabled="isGithubSynced" />
                </FormGroup>
              </UTooltip>
              <UTooltip
                :text="SYNC_DISABLED_TOOLTIP"
                :prevent="!isGithubSynced"
                class="block"
                :ui="{ base: 'h-auto !whitespace-normal max-w-xs' }"
              >
                <FormGroup
                  label="Parent team"
                  size="md"
                  name="parent"
                  class="mt-4"
                  :disabled="isGithubSynced || isLoadingTeamDetails || isCreatingTeam || isUpdatingTeam"
                >
                  <SelectMenu
                    v-model="state.parentTeam.id"
                    :is-fetching="isFetchingTeams"
                    :searchable="searchTeams"
                    :clearable="Boolean(state.parentTeam.id) && !isGithubSynced"
                    :disabled="isGithubSynced"
                    placeholder="Not selected"
                    option-attribute="name"
                    value-attribute="id"
                    selected-icon="light:check"
                    size="md"
                    @clear="clearParent"
                  />
                </FormGroup>
              </UTooltip>
            </div>

            <!-- Subsections (kept mounted with v-show so their queries stay alive for cache invalidation) -->
            <TeamModalMembers
              v-show="activeSection === 'members'"
              ref="members"
              :team-id="id"
              :is-github-synced="isGithubSynced"
            />
            <TeamModalBots v-show="activeSection === 'bots'" ref="bots" :team-id="id" />
            <TeamModalRepositories
              v-show="activeSection === 'repositories'"
              ref="repos"
              :team-id="id"
              :is-github-synced="isGithubSynced"
            />
          </UForm>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <UButton size="sm" variant="ghost" color="gray" :disabled="isSaving" @click="open = false"> Cancel </UButton>
          <UButton size="sm" :loading="isSaving" :disabled="isLoadingData || isSaving" @click="form?.submit()">
            Save
          </UButton>
        </div>
      </template>
    </UCard>
  </UModal>
</template>
